import { lazy, Suspense } from "react";
import { Button } from "@/components/ui/button";
import { ArrowDown, ShieldCheck } from "lucide-react";
import { motion } from "framer-motion";
const caretexLogo = "/caretex-logo-transparent.png";

const DNAHelix = lazy(() => import("@/components/ui/DNAHelix"));
const HeartbeatLine = lazy(() => import("@/components/ui/HeartbeatLine"));
const CircuitBoard = lazy(() => import("@/components/ui/CircuitBoard"));

const Hero = () => {
  const scrollToContact = () => {
    const contactSection = document.getElementById("contact");
    contactSection?.scrollIntoView({ behavior: "smooth" });
  };

  const scrollToContent = () => {
    const problemSection = document.getElementById("problem");
    problemSection?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-primary">
      {/* Lazy-loaded decorations */}
      <Suspense fallback={null}>
        <CircuitBoard />
      </Suspense>
      
      {/* Grid background */}
      <div className="absolute inset-0 bg-[linear-gradient(to_right,hsl(168,80%,35%,0.03)_1px,transparent_1px),linear-gradient(to_bottom,hsl(168,80%,35%,0.03)_1px,transparent_1px)] bg-[size:60px_60px]" />
      
      {/* Animated gradient orbs */}
      <motion.div 
        className="absolute top-1/4 left-1/4 w-[600px] h-[600px] bg-secondary/15 rounded-full blur-3xl"
        animate={{ scale: [1, 1.2, 1], opacity: [0.1, 0.2, 0.1] }}
        transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
      />
      <motion.div 
        className="absolute bottom-1/4 right-1/4 w-[500px] h-[500px] bg-accent/15 rounded-full blur-3xl"
        animate={{ scale: [1.2, 1, 1.2], opacity: [0.1, 0.2, 0.1] }}
        transition={{ duration: 8, repeat: Infinity, ease: "easeInOut", delay: 2 }}
      />

      {/* DNA Helix decorations - lazy loaded */}
      <Suspense fallback={null}>
        <DNAHelix className="left-10 top-1/4 hidden lg:block" />
        <DNAHelix className="right-10 bottom-1/4 hidden lg:block" />
      </Suspense>
      
      {/* Heartbeat lines - lazy loaded */}
      <Suspense fallback={null}>
        <HeartbeatLine className="top-16 left-1/4 hidden md:block" />
        <HeartbeatLine className="bottom-32 right-1/4 hidden md:block" />
        <div className="md:hidden absolute top-28 left-0 right-0 flex justify-center">
          <HeartbeatLine className="relative scale-90" />
        </div>
      </Suspense>

      {/* Content */}
      <div className="relative z-10 container mx-auto px-6 text-center">
        <div className="max-w-4xl mx-auto">
          {/* Logo */}
          <div className="mb-6">
            <div className="relative inline-block">
              <div className="absolute inset-0 blur-3xl bg-secondary/30 opacity-30" />
              <img 
                src={caretexLogo} 
                alt="CARETEX" 
                width={288}
                height={288}
                fetchPriority="high"
                className="relative h-48 md:h-60 lg:h-72 w-auto mx-auto object-contain drop-shadow-2xl"
              />
            </div>
          </div>

          {/* Badge */}
          <motion.div 
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 mb-8"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            <ShieldCheck className="w-5 h-5 text-secondary" />
            <span className="text-base font-medium text-white/90">נבנתה על ידי רופאים ומנהלים מהשטח</span>
          </motion.div>

          {/* Main Heading */}
          <motion.h1 
            className="text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold mb-6 leading-tight text-white"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.5 }}
          >
            הדור הבא של
            <br />
            <span className="text-secondary">ניהול רפואי ומוסדי</span>
          </motion.h1>

          {/* Subtitle */}
          <motion.p 
            className="text-xl md:text-2xl text-white/80 max-w-2xl mx-auto mb-10 leading-relaxed"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.7 }}
          >
            מערכת אחת מקיפה לניהול מוסדות טיפוליים - רפואה, תפעול, צוות, רגולציה.
            <span className="block mt-2 font-semibold text-white text-2xl md:text-3xl">הכל במקום אחד.</span>
          </motion.p>

          {/* CTA Buttons */}
          <motion.div 
            className="flex flex-col sm:flex-row gap-4 justify-center items-center"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.9 }}
          >
            <Button 
              onClick={scrollToContact}
              size="lg"
              className="bg-accent hover:bg-accent/90 text-accent-foreground font-bold px-8 py-6 text-lg"
            >
              השאירו פרטים ונחזור אליכם
            </Button>
            <Button 
              onClick={scrollToContent}
              variant="outline"
              size="lg"
              className="border-white/30 text-white hover:bg-white/10 hover:border-secondary/50 px-8 py-6 text-lg backdrop-blur-sm"
            >
              גללו למטה לפרטים נוספים
              <ArrowDown className="w-5 h-5 mr-2" />
            </Button>
          </motion.div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <motion.div 
        className="absolute bottom-8 left-1/2 transform -translate-x-1/2"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.2 }}
      >
        <button 
          onClick={scrollToContent}
          aria-label="גלול למטה"
          className="w-8 h-12 border-2 border-white/30 rounded-full flex justify-center hover:border-secondary/50 transition-colors"
        >
          <motion.div 
            className="w-1.5 h-3 bg-white/70 rounded-full mt-2"
            animate={{ y: [0, 8, 0] }}
            transition={{ duration: 1.5, repeat: Infinity }}
          />
        </button>
      </motion.div>
    </section>
  );
};

export default Hero;
